// barCrop.js — PURE, framework-free geometry for the Title Bar's CROP-TO-BAR
// marquee (the deck-15 delta's largest novel component).
//
// Deck (source of truth):
//   design-handoff/concepts/builder-blocks/15-titlebar-block-editor.html
//   "Crop to bar · Cropping tool — R7 · 9:1 — locked ·
//    Drag the region — it fits your 52 px / Full-width bar, never stretched."
//
// NON-DESTRUCTIVE by design: nothing is uploaded and no derived file is written.
// The author's chosen region is stored as a NORMALISED rect (0..1 of the source
// image) and rendered by zooming the background to that region — so the original
// image is untouched, the crop is re-editable forever, and turning the flag off
// simply ignores it.
//
// The rect is normalised (fractions of the image), which makes it resolution-
// independent; the 9:1 ASPECT LOCK lives in the marquee UI (cropperjs
// `aspectRatio`), which works in real pixels and knows the image's dimensions.
// This module therefore validates and RENDERS a rect — it never guesses an
// aspect from normalised numbers, which would be wrong for any non-square image.

// The bar is a 9:1 band (deck: "9:1 — locked").
export const BAR_ASPECT = 9;

// The whole image — the identity crop, and the fallback for anything invalid.
export const FULL_CROP = { x: 0, y: 0, w: 1, h: 1 };

const clamp01 = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return null;
  return Math.min(1, Math.max(0, x));
};

/**
 * Coerce a stored crop into a valid normalised rect inside the image, or return
 * the full-image identity crop. A rect that starts inside but overflows the edge
 * is TRIMMED to the edge rather than rejected (a resize that ran past the border
 * should still crop, not silently revert to the whole image).
 *
 * @param {*} raw stored crop prop
 * @returns {{x:number,y:number,w:number,h:number}}
 */
export const normalizeCrop = (raw) => {
  if (!raw || typeof raw !== "object") return FULL_CROP;
  const x = clamp01(raw.x);
  const y = clamp01(raw.y);
  const w = clamp01(raw.w);
  const h = clamp01(raw.h);
  if (x === null || y === null || w === null || h === null) return FULL_CROP;
  const width = Math.min(w, 1 - x);
  const height = Math.min(h, 1 - y);
  if (width <= 0 || height <= 0) return FULL_CROP;
  return { x, y, w: width, h: height };
};

/** True when the crop actually selects a sub-region (i.e. is worth rendering). */
export const isCropped = (raw) => {
  const c = normalizeCrop(raw);
  return c.w < 1 || c.h < 1;
};

/**
 * Convert a cropperjs pixel rect + the source image's natural size into the
 * normalised rect we persist. Out-of-canvas values (cropperjs allows a box to
 * extend past the image in some view modes) are clamped by normalizeCrop.
 *
 * @param {{x:number,y:number,width:number,height:number}} data cropperjs getData()
 * @param {{width:number,height:number}} natural source image natural size
 * @returns {{x:number,y:number,w:number,h:number}}
 */
export const cropFromPixels = (data, natural) => {
  const nw = Number(natural && natural.width);
  const nh = Number(natural && natural.height);
  if (!Number.isFinite(nw) || !Number.isFinite(nh) || nw <= 0 || nh <= 0)
    return FULL_CROP;
  const d = data || {};
  return normalizeCrop({
    x: Number(d.x) / nw,
    y: Number(d.y) / nh,
    w: Number(d.width) / nw,
    h: Number(d.height) / nh,
  });
};

/** The pixel rect a stored crop maps back to — used to re-open the marquee. */
export const cropToPixels = (raw, natural) => {
  const nw = Number(natural && natural.width);
  const nh = Number(natural && natural.height);
  const c = normalizeCrop(raw);
  if (!Number.isFinite(nw) || !Number.isFinite(nh) || nw <= 0 || nh <= 0)
    return { x: 0, y: 0, width: 0, height: 0 };
  return {
    x: Math.round(c.x * nw),
    y: Math.round(c.y * nh),
    width: Math.round(c.w * nw),
    height: Math.round(c.h * nh),
  };
};

const pct = (n) => `${Math.round(n * 1000) / 10}%`;

/**
 * Render a normalised crop as background CSS: zoom the image so the chosen
 * region exactly fills the band, and position that region — the "never
 * stretched" promise (both axes scale by the SAME rule the browser's `cover`
 * uses; we only choose WHICH part of the image is shown).
 *
 * The position math is the standard background-position percentage identity:
 * a region starting at x with width w sits at x / (1 - w) of the travel.
 *
 * @param {*} raw stored crop
 * @returns {{backgroundSize:string, backgroundPosition:string, backgroundRepeat:string}|null}
 *          null when the crop is the whole image (nothing to override)
 */
export const cropToBackgroundStyle = (raw) => {
  const c = normalizeCrop(raw);
  if (c.w >= 1 && c.h >= 1) return null;
  const sizeX = 1 / c.w;
  const sizeY = 1 / c.h;
  const posX = c.w >= 1 ? 0 : c.x / (1 - c.w);
  const posY = c.h >= 1 ? 0 : c.y / (1 - c.h);
  return {
    backgroundSize: `${pct(sizeX)} ${pct(sizeY)}`,
    backgroundPosition: `${pct(posX)} ${pct(posY)}`,
    backgroundRepeat: "no-repeat",
  };
};

/** A human summary of the crop for the editor + the marquee's accessible name. */
export const describeCrop = (raw) => {
  const c = normalizeCrop(raw);
  if (c.w >= 1 && c.h >= 1) return "Whole image";
  return `${Math.round(c.w * 100)}% × ${Math.round(c.h * 100)}% of the image`;
};

const barCrop = {
  BAR_ASPECT,
  FULL_CROP,
  normalizeCrop,
  isCropped,
  cropFromPixels,
  cropToPixels,
  cropToBackgroundStyle,
  describeCrop,
};

export default barCrop;
